import { useMemo } from "react";
import { useACLContext } from "../context";
import type { PermissionAction, ResourceType } from "@shield-acl/core";
import type { PermissionCheck } from "./useCanAny";

/**
 * Hook que verifica se o usuário tem TODAS as permissões listadas (AND)
 *
 * @param permissions - Array de permissões para verificar
 * @returns boolean indicando se tem todas as permissões
 *
 * @example
 * ```tsx
 * // Verificar se pode criar E editar posts (ambas necessárias)
 * const canFullyManagePosts = useCanAll([
 *   { action: "posts.create" },
 *   { action: "posts.edit" },
 *   { action: "posts.delete" }
 * ])
 *
 * // Verificar múltiplas permissões com recursos
 * const hasFullAccess = useCanAll([
 *   { action: "users.read", resource: "users" },
 *   { action: "users.write", resource: "users" },
 *   { action: "settings.manage" }
 * ])
 *
 * // Com contexto específico
 * const canFullyControlPost = useCanAll([
 *   { action: "posts.edit", resource: "posts", context: { postId: 123 } },
 *   { action: "posts.publish", resource: "posts", context: { postId: 123 } },
 *   { action: "posts.delete", resource: "posts", context: { postId: 123 } }
 * ])
 * ```
 */
export function useCanAll<TContext = any>(
  permissions: PermissionCheck<TContext>[],
): boolean {
  const { can, user } = useACLContext<TContext>();

  // Memoiza o resultado baseado nas permissões e no usuário
  return useMemo(() => {
    // Se não há permissões para verificar, retorna true (vazio = todas satisfeitas)
    if (!permissions || permissions.length === 0) {
      return true;
    }

    // Verifica se tem TODAS as permissões
    return permissions.every((permission) =>
      can(permission.action, permission.resource, permission.context),
    );
  }, [can, permissions, user]);
}

/**
 * Versão simplificada do useCanAll que aceita apenas ações como strings
 *
 * @param actions - Array de ações para verificar
 * @returns boolean indicando se tem todas as permissões
 *
 * @example
 * ```tsx
 * // Verificação simples de múltiplas ações
 * const hasCompleteUserAccess = useCanAllActions([
 *   "users.read",
 *   "users.create",
 *   "users.edit",
 *   "users.delete"
 * ])
 * ```
 */
export function useCanAllActions(actions: PermissionAction[]): boolean {
  const permissions = useMemo(
    () => actions.map((action) => ({ action })),
    [actions],
  );

  return useCanAll(permissions);
}
